import { animate, state, style, transition, trigger } from '@angular/animations';
import { DatePipe, NgClass } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { Store } from '@ngrx/store';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { takeUntil } from 'rxjs/operators';
import { IdentityService } from 'src/app/core/identity.service';
import { PerDiemRecruitingService } from 'src/app/perdiem-recruiting/perdiem-recruiting.service';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { FollowupPickerBaseComponent } from 'src/app/shared/components/followup-picker/followup-picker-base.component';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { FollowupTime } from 'src/app/shared/models/followup-time.model';
import { PendoEventFollowUpAdded, PendoEventFollowUpMarkedAsCompleted } from 'src/app/shared/models/pendo/pendo-events';
import { PerDiemFollowupView } from 'src/app/shared/models/perdiem-followup-view.model';
import { IsEmptyPipe } from 'src/app/shared/pipes/is-empty.pipe';
import { ConfirmDialogComponent } from 'src/app/shared/services/confirm-dialog.service';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { PendoService } from 'src/app/shared/services/pendo.service';

@Component({
    selector: 'nova-add-perdiem-followup-picker',
    templateUrl: './add-perdiem-followup-picker.component.html',
    styleUrls: ['./add-perdiem-followup-picker.component.scss'],
    animations: [
        trigger('balloonEffect', [
            state(
                'initial',
                style({
                    opacity: 0
                })
            ),
            state(
                'final',
                style({
                    opacity: 1
                })
            ),
            transition('initial=>final', animate('1500ms')),
            transition('final=>initial', animate('1500ms'))
        ])
    ],
    standalone: true,
    providers: [provideNgxMask()],
    imports: [
        MatLegacyMenuModule,
        MatLegacyFormFieldModule,
        MatLegacySelectModule,
        MatLegacyInputModule,
        MatLegacyButtonModule,
        MatIconModule,
        ReactiveFormsModule,
        NgClass,
        DatePipe,
        IsEmptyPipe,
        NgxMaskDirective,
        CalendarComponent
    ]
})
export class AddPerDiemFollowupPickerComponent extends FollowupPickerBaseComponent implements OnInit, OnChanges {
    @Input() followUpTimes: FollowupTime[];
    @Input() currentFollowup: PerDiemFollowupView;
    @ViewChild('menuTrigger') matMenu;
    readonly featureFlag = FeatureFlag;
    constructor(
        private readonly perDiemRecruitingService: PerDiemRecruitingService,
        private readonly identityService: IdentityService,
        private readonly _store: Store,
        private readonly _dialog: MatDialog,
        private readonly _pendoService: PendoService,
        protected readonly _featureManager: LDFeatureManager
    ) {
        super(_store, _featureManager);
    }

    ngOnInit(): void {
        this.recruiterId = this.identityService.current.contactId;
        this.initForm();
        this.selectedDates.push(this.currentDate);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.currentFollowup?.currentValue) {
            this.changeInProgress = true;
            this.initForm();
            this.changeInProgress = false;
        }
    }

    protected initLookups(): void {
        this.getLookups(this.currentDate, this.perDiemRecruitingService, false);
    }

    public selectedDateChanged(newDate: Date): void {
        this.handleSelectedDateChanged(newDate, this.perDiemRecruitingService);
    }

    protected getFollowUpTimes(): void {
        if (this.hours.length === 1) {
            this.setFollowUpTimes(this.followUpTimes);
        }
    }

    saveFollowUp(): void {
        if (!this.saveInProgress && !this.changeInProgress && this.resourcesForm?.value?.date) {
            this.saveInProgress = true;
            this.matMenu.closeMenu();
            const follow = this.createDto();
            this.updateRecruiterResources(this.perDiemRecruitingService, follow);
        }
    }

    protected createDto(): PerDiemFollowupView {
        return this.createPerDiemFollowUpDto(this.currentFollowup.id);
    }

    protected handleSave(data: PerDiemFollowupView): void {
        this.resetDefaultValues();
        this.resourcesForm.markAsPristine();
        this.saveInProgress = false;
        this.currentFollowup = data;
        this.onChangeFollowup.emit([data]);

        const event = new PendoEventFollowUpAdded({
            id: data.id,
            date: data.followupDate,
            priority: data.priority
        });

        this._pendoService.trackEvent(event);
    }

    openMarkAsCompleteDialog(): void {
        const dialogRef = this._dialog.open(ConfirmDialogComponent, {
            data: {
                message:
                    'Are you sure you want to complete this follow-up? The date, time, <br /> priority, and reason will be reset.',
                action: 'MARK AS COMPLETE',
                title: 'Complete Follow-Up',
                needCancel: true,
                needCross: false,
                cancelText: 'CANCEL'
            },
            panelClass: 'nova__dialog'
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this._cleanup$))
            .subscribe((result) => {
                if (result) {
                    this.requestMarkAsComplete(this.perDiemRecruitingService, this.currentFollowup);
                }
            });
    }

    protected markAsComplete(): void {
        const event = new PendoEventFollowUpMarkedAsCompleted({
            id: this.currentFollowup.id,
            date: this.currentFollowup.followupDate.toString(),
            priority: this.currentFollowup.priority
        });

        this._pendoService.trackEvent(event);

        this.currentFollowup.id = null;
        this.currentFollowup.followupDate = null;
        this.currentFollowup.priority = null;
        this.onChangeFollowup.emit([]);
    }
}
